<script setup lang="ts">
import type { CmsElementCategoryBanner } from "~/composables/useCustom";
import { useCategory } from "#imports";
import { ref, onMounted } from 'vue';

const props = defineProps<{ content: CmsElementCategoryBanner }>();
const { category } = useCategory();
const { imageDesktop, imageTablet, imageMobile, categoryTitle, categorySubtitle, categoryDescription } = props.content.data;

const isMounted = ref(false);

onMounted(() => {
  setTimeout(() => {
    isMounted.value = true;
  }, 400);
});
</script>

<template>
  <div class="listing-banner bg-gradient-to-r from-gray-300 to-gray-200 relative h-[220px] md:h-[280px] 2xl:h-[320px] 3xl:h-[369px] max-h-[369px] overflow-hidden" role="region" :aria-label="$t('productListing.ariaLabelCategoryBanner')">
    <SharedImage
      v-if="imageDesktop || imageTablet || imageMobile || category.media?.url"
      :src="imageDesktop?.media?.url ? imageDesktop?.media?.url : category.media?.url"
      :alt="imageDesktop?.media?.alt ? imageDesktop?.media?.alt : 'my sofa bed'"
      :width="imageDesktop?.media?.metaData?.width ?? category.media?.metaData?.width"
      :height="imageDesktop?.media?.metaData?.height ?? category.media?.metaData?.height"
      loading="eager"
      format="webp"
      imgClass="transition-opacity duration-1000 fade-in-up staggered-item w-full h-full object-cover aspect-video md:aspect-auto"
      :class="{ 'is-mounted': isMounted }"
      :sizes="{
        desktop: '1905x369',
        tablet: '920x403',
        mobile: '744x480'
      }"
      :sources="{
        desktop: imageDesktop?.media,
        tablet: imageTablet?.media,
        mobile: imageMobile?.media
      }"
    />
    <div class="banner-content absolute inset-0 h-full bg-gradient-to-r from-black/50 to-transparent">
      <div class="container w-full h-full flex flex-col justify-center items-start relative text-white">
        <div :class="['content-wrap transition-all duration-700 transform fade-in-up staggered-item w-full', { 'is-mounted': isMounted }]">
          <span class="text-base 3xl:text-md font-normal font-marcellus" v-if="categorySubtitle || category">{{ categorySubtitle ? categorySubtitle : '' }}</span>
          <h1 class="text-7xl lg:text-11xl 3xl:text-20xl leading-none my-2" v-if="categoryTitle || category.translated.name">{{ categoryTitle ? categoryTitle : category.translated.name }}</h1>
          <div class="mb-0 text-base 3xl:text-md font-normal max-w-full md:max-w-[80%] line-clamp-3" v-if="categoryDescription || category.translated.description" v-html="categoryDescription ? categoryDescription : category?.translated?.description">
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.banner-content {
  will-change: transform, opacity;
}

.fade-in-up {
  opacity: 0;
  transform: translateY(20px);
}

.fade-in-up.is-mounted {
  animation: fadeInUp 0.6s ease forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.staggered-item.is-mounted {
  animation-delay: calc(var(--index) * 50ms);
}
</style>